import { useQueries } from "@tanstack/react-query";
import invoiceByAppointmentIdsQueryOptions from "queries/invoiceByAppointmentIdsQueryOptions";
import { useMemo } from "react";

const PAGE_SIZE = 100;

const useFetchInvoicesForAppointments = <T extends AppointmentMetaData>(
  appointments: T[],
  onBehalfOf?: string,
) => {
  const appointmentIdPages = useMemo(() => {
    const pages: string[][] = [];
    for (let i = 0; i < appointments.length; i += PAGE_SIZE) {
      pages.push(
        appointments
          .slice(i, i + PAGE_SIZE)
          .filter((el) => el.statusFlags.usingInvoicing)
          .map((el) => el.appointment_id.toString()),
      );
    }
    return pages.filter((page) => page.length > 0);
  }, [appointments]);

  const invoicesQueries = useQueries({
    queries: appointmentIdPages.map((page) =>
      invoiceByAppointmentIdsQueryOptions({
        appointmentIds: page,
        onBehalfOf,
      }),
    ),
  });

  const invoices = useMemo(() => {
    return invoicesQueries.flatMap((query) => query.data?.invoices ?? []);
  }, [invoicesQueries]);

  const appointmentsWithInvoices = useMemo(() => {
    const invoiceByAppointmentId = invoices.reduce((acc, invoice) => {
      return { ...acc, [invoice.appointment_id.toString()]: invoice };
    }, {} as Record<string, InvoiceData>);
    return appointments.map((appointment) => {
      const invoice =
        invoiceByAppointmentId[appointment.appointment_id.toString()];
      return {
        ...appointment,
        invoice,
      };
    });
  }, [appointments, invoices]);

  return {
    isLoadingInvoices: invoicesQueries.some(
      (query) => query.isLoading || query.isPending,
    ),
    appointmentsWithInvoices,
  };
};

export default useFetchInvoicesForAppointments;
